/**
 * workSheetParts.js
 *
 * Pure helpers for the work-sheet part lines: backend <-> local mapping, the
 * purchase-price / margin maths and the "may this user see purchase prices"
 * check. No React, no React-Native imports: unit-testable on their own.
 *
 * @module helpers/workSheetParts
 */

const isCost = (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0;

const toCost = (value) => {
    if (value === null || value === undefined || value === '' || typeof value === 'boolean') return undefined;
    const num = Number(value);

    return isCost(num) ? num : undefined;
};

const roundTo1 = (value) => Math.round(value * 10) / 10;
const roundTo2 = (value) => Math.round(value * 100) / 100;

const OBJECT_ID = /^[a-f0-9]{24}$/i;

/**
 * An id that the backend can cast to an ObjectId, else `undefined`. A work-order PATCH carrying anything else in
 * `itemLines[].itemId` fails validation as a whole (and the phone would only keep the change locally).
 *
 * @param {*} value Candidate id.
 * @returns {string|undefined} The 24-hex id.
 */
const asObjectId = (value) => {
    const text = typeof value === 'string' ? value.trim() : '';

    return OBJECT_ID.test(text) ? text : undefined;
};

/**
 * Localized label out of a string or `{hu, en, ...}` object (hu, then en, then first value).
 *
 * @param {*} value Raw name.
 * @returns {string}
 */
const pickName = (value) => {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object') {
        const first = Object.values(value).find((v) => typeof v === 'string' && v);

        return value.hu || value.en || first || '';
    }

    return '';
};

const cleanSku = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);

    return typeof value === 'string' ? value.trim() : '';
};

/**
 * Article number ("cikkszám") of a catalogue article: its own `sku`, else `code`, else the first size that carries a
 * `sku`, and only then the product barcode — the order the admin work-order table names a part by, so both clients
 * tell the same part by the same number.
 *
 * @param {object} food Food document (full or `basicDetails`).
 * @returns {string|undefined} Trimmed article number; undefined when the article has none.
 */
export const resolveFoodSku = (food) => {
    if (!food || typeof food !== 'object') return undefined;

    const sizes = Array.isArray(food.sizes) ? food.sizes : [];

    for (const candidate of [food.sku, food.code, ...sizes.map((size) => size?.sku), food.barcode]) {
        const sku = cleanSku(candidate);

        if (sku) return sku;
    }

    return undefined;
};

/**
 * Map the backend lines of a work order to local part rows.
 *
 * `itemId` is the catalogue article's id and nothing else: a line typed by hand has none (it used to inherit the
 * line's own `_id`, which is not an article id — sent back on the next save it became a dangling reference).
 * The `name` and the article number (`sku`) saved with the line win over the populated article's.
 *
 * @param {object} wo Work order.
 * @returns {object[]}
 */
export const normalizeItemLines = (wo) => {
    const lines = Array.isArray(wo?.itemLines)
        ? wo.itemLines
        : Array.isArray(wo?.parts) ? wo.parts : [];

    return lines.map((ln) => {
        const populated = ln.itemId && typeof ln.itemId === 'object' ? ln.itemId : null;
        const itemId = populated ? (populated._id ? String(populated._id) : undefined) : (ln.itemId || undefined);

        return {
            _id: ln._id || itemId || `${Date.now()}-${Math.random()}`,
            itemId,
            name: ln.name || ln.itemName || (populated ? pickName(populated.name) : ''),
            sku: cleanSku(ln.sku) || resolveFoodSku(populated),
            qty: Number(ln.qty ?? ln.quantity ?? 0) || 0,
            unitPrice: Number(ln.unitPrice ?? ln.price ?? 0) || 0,
            type: ln.type || 'part',
            costPrice: toCost(ln.costPrice),
            costPriceSource: ln.costPriceSource,
        };
    });
};

/**
 * Map local part rows to the backend `itemLines` payload.
 *
 * A part picked from the catalogue carries its article as `foodId` (a saved one as `itemId`); both are sent as
 * `itemId` so the part keeps its article — name, VAT, purchase-price snapshot, and a line on the sales order — instead
 * of being stored as an anonymous quantity × price. A part typed by hand has no article and is identified by its `name`.
 * The article number (`sku`, "cikkszám") is sent whenever the part has one, so it is saved with the line.
 *
 * @param {object[]} parts Local parts.
 * @returns {object[]}
 */
export const toBackendItemLines = (parts = []) => {
    if (!Array.isArray(parts)) return [];

    return parts.map((part) => {
        const cost = toCost(part.costPrice);
        const itemId = asObjectId(part.itemId) || asObjectId(part.foodId);
        const sku = cleanSku(part.sku);

        return {
            ...(itemId ? { itemId } : {}),
            quantity: Number(part.qty ?? part.quantity ?? 0) || 0,
            price: Number(part.unitPrice ?? part.price ?? 0) || 0,
            ...(part.name ? { name: part.name } : {}),
            ...(sku ? { sku } : {}),
            ...(cost !== undefined ? { costPrice: cost } : {}),
        };
    });
};

/** VAT percent assumed for a part that does not carry its own (Hungarian standard rate). */
export const DEFAULT_VAT_RATE = 27;

const vatRateOf = (part) => {
    for (const raw of [part?.vatRate, part?.vatNo, part?.vat]) {
        if (raw !== null && raw !== undefined && raw !== '') {
            const num = Number(raw);

            if (Number.isFinite(num) && num >= 0) return num;
        }
    }

    return DEFAULT_VAT_RATE;
};

/**
 * Margin of one part line, on the NET selling price.
 *
 * The platform stores selling prices gross (VAT-inclusive — the invoice and sales-order builders derive the net from
 * them) and purchase prices net, exactly like the cost-intelligence `computeMargin`; comparing the raw numbers would
 * overstate the profit by the VAT. So the selling price is converted to net with the part's VAT rate (default 27 %)
 * before the purchase price is deducted. `markupPct` ("haszonkulcs") is that profit as a percentage of the purchase
 * price.
 *
 * @param {object} part Local part (`qty`, `unitPrice` gross, `costPrice` net, optional `vatRate`).
 * @returns {null|object} null when the purchase price is unknown.
 */
export const getPartMargin = (part) => {
    const unitCost = toCost(part?.costPrice);

    if (unitCost === undefined) return null;

    const qty = Number(part?.qty ?? part?.quantity ?? 0) || 0;
    const unitPrice = Number(part?.unitPrice ?? part?.price ?? 0) || 0;
    const vatRate = vatRateOf(part);
    const netUnitPrice = unitPrice / (1 + (vatRate / 100));
    const unitProfit = netUnitPrice - unitCost;

    return {
        qty,
        unitCost,
        unitPrice,
        vatRate,
        netUnitPrice: roundTo2(netUnitPrice),
        unitProfit: roundTo2(unitProfit),
        markupPct: unitCost > 0 ? roundTo1((unitProfit / unitCost) * 100) : null,
        costTotal: roundTo2(unitCost * qty),
        saleTotal: roundTo2(unitPrice * qty),
        saleNetTotal: roundTo2(netUnitPrice * qty),
        profit: roundTo2(unitProfit * qty),
    };
};

/**
 * Summary over the parts that have a purchase price (net selling total vs net purchase total).
 *
 * @param {object[]} parts Local parts.
 * @returns {{costTotal: number, saleTotal: number, saleNetTotal: number, profit: number, markupPct: (number|null), counted: number, missing: number}}
 */
export const getPartsMarginSummary = (parts) => {
    let costTotal = 0;
    let saleTotal = 0;
    let saleNetTotal = 0;
    let counted = 0;
    let missing = 0;

    (Array.isArray(parts) ? parts : []).forEach((part) => {
        const m = getPartMargin(part);

        if (!m) {
            missing += 1;

            return;
        }
        costTotal += m.costTotal;
        saleTotal += m.saleTotal;
        saleNetTotal += m.saleNetTotal;
        counted += 1;
    });

    const profit = saleNetTotal - costTotal;

    return {
        costTotal: roundTo2(costTotal),
        saleTotal: roundTo2(saleTotal),
        saleNetTotal: roundTo2(saleNetTotal),
        profit: roundTo2(profit),
        markupPct: costTotal > 0 ? roundTo1((profit / costTotal) * 100) : null,
        counted,
        missing,
    };
};

const ELEVATED_ROLES = [
    'serviceAdmin', 'serviceAdvisor', 'system:admin', 'restaurantStaff:admin',
    'restaurantStaff:manager', 'restaurantAdmin', 'restaurantOwner',
];
const RESTRICTED_ROLES = ['serviceTechnician', 'serviceExternal', 'serviceViewer'];

const collectRoles = (user) => {
    const out = [];

    ['roles', 'userRoles', 'role', 'defaultRoles', 'effectiveRoles'].forEach((key) => {
        const value = user[key];

        (Array.isArray(value) ? value : [value]).forEach((r) => {
            if (typeof r === 'string' && r) out.push(r);
        });
    });

    return out;
};

/**
 * May this user see purchase prices / margins? Fails closed for a missing user.
 *
 * @param {object} user User info.
 * @returns {boolean}
 */
export const canViewPurchasePrice = (user) => {
    if (!user || typeof user !== 'object') return false;

    const roles = collectRoles(user);

    if (roles.some((r) => ELEVATED_ROLES.includes(r))) return true;

    return !roles.some((r) => RESTRICTED_ROLES.includes(r));
};
